import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  useMutation,
  MutationKey,
  MutateOptions,
} from "react-query";

import {
  usersService,
  userDetailService,
  userCreateService,
  userUpdateService,
  userSetPasswordService,
} from "./services";
import { User, UserSetPasswordRequest } from "./types";

type UsersListResponse = Awaited<ReturnType<typeof usersService>>;
type UserDetailResponse = Awaited<ReturnType<typeof userDetailService>>;

export const useUsers = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof usersService>[0];
  options?: QueryObserverOptions<unknown, unknown, UsersListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "userHooks",
    queryFn: () => usersService(params),
    ...options,
  });

  return response as UseQueryResult<UsersListResponse>;
};

export const useUserDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof userDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, UserDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "userDetailHooks",
    queryFn: () => userDetailService(params),
    ...options,
  });

  return response as UseQueryResult<UserDetailResponse>;
};

export const useUserUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<User, Parameters<typeof userUpdateService>>;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "titleUpdateHooks",
    mutationFn: userUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useUserCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<User, Parameters<typeof userCreateService>>;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "titleUpdateHooks",
    mutationFn: userCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useUserSetPassword = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<unknown, unknown>;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "userSetPasswordHooks",
    mutationFn: (params: { id: number; data: UserSetPasswordRequest }) =>
      userSetPasswordService(params.id, params.data),
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
